import type { UserDataInput } from '$/API';
import type { RegistrationSchemaType, SignUpSchemaType } from '../stepsMap';

import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter } from 'next/router';
import { useContext, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import * as Sentry from '@sentry/nextjs'

import { QuestionPageContext } from '../QuestionPageProvider';
import { signUpSchema } from '../stepsMap';
import { useRegistrationFeedback } from '../useRegistrationFeedback';

import ApiClient from '$/ApiClient';
import theme from '$/assets/theme';
import HeadContentSection from '$/components/blocks/HeadContentSection';
import StudentQuestion from '$/components/blocks/StudentQuestion';
import BorderBottomSpan from '$/components/elements/BorderBottomSpan';
import Button from '$/components/elements/Button';
import ButtonContainer from '$/components/elements/ButtonContainer';
import Container from '$/components/elements/Container';
import Loader from '$/components/elements/Loader';
import CheckboxInputQuestion from '$/components/elements/Questions/CheckboxInputQuestion';
import DateInputQuestion from '$/components/elements/Questions/DateInputQuestion';
import EmailInputQuestion from '$/components/elements/Questions/EmailnputQuestion';
import genderList from '$/components/elements/Questions/lists/genderList';
import RadioInputQuestion from '$/components/elements/Questions/RadioInputQuestion';
import TextInputQuestion from '$/components/elements/Questions/TextInputQuestion';
import {
  H4Typography,
  H5Typography,
  H6Typography,
  Sub2Typography,
} from '$/components/elements/Typography';
import useElb from '$/hooks/useElb';
import useMaximumFinancing from '$/hooks/useMaximumFinancing';
import ErrorMessages from '$/types/enums/ErrorMessages';
import TestTypes from '$/types/enums/TestTypes';
import { cognitoSignIn } from '$/utils/cognitoAuthentication';
import dateToISOLikeString from '$/utils/dateToISOLikeString';
import formatPrice from '$/utils/formatPrice';
import handleCommonToast from '$/utils/handleCommonToast';
import { exportToLocalStorage } from '$/utils/localstorage';
import { TRACKING_ACTION } from '$/utils/tracking/consts';

const mapQueryInput = (
  dataToSave: RegistrationSchemaType,
  dbTrackingId: string,
  campaignToken: string
): UserDataInput => {
  const data: UserDataInput = {
    email: dataToSave.email,
    firstName: dataToSave.firstName,
    lastName: dataToSave.lastName,
    birthDate: dataToSave?.birthDate
      ? dateToISOLikeString(dataToSave.birthDate)
      : null,
    previousEducation: Number(dataToSave.previousEducation?.[0]?.id),
    schoolNote: dataToSave.schoolNote,
    startDate: dataToSave?.startDate
      ? dateToISOLikeString(dataToSave.startDate)
      : null,
    endDate: dataToSave?.endDate
      ? dateToISOLikeString(dataToSave.endDate)
      : null,
    expectedDegreeId: Number(dataToSave.expectedDegree?.[0]?.id),
    variantId: Number(dataToSave.variant?.[0]?.id),
    salutationId: Number(dataToSave.gender),
    cityAlt: dataToSave.city?.[0]?.id.toString(),
    dbTrackingId,
    campaignToken,
  };

  if (dataToSave.major?.[0]?.id === dataToSave.major?.[0]?.value) {
    data.majorId = -1;
    data.majorAlt = dataToSave.major?.[0]?.value;
  } else {
    data.majorId = Number(dataToSave.major?.[0]?.id);
  }

  if (dataToSave.field?.[0]?.id === dataToSave.field?.[0]?.value) {
    data.subjectId = -1;
    data.subjectAlt = dataToSave.field?.[0]?.value;
  } else {
    data.subjectId = Number(dataToSave.field?.[0]?.id);
  }

  if (dataToSave.university?.[0]?.id === dataToSave.university?.[0]?.value) {
    data.universityId = -1;
    data.universityAlt = dataToSave.university?.[0]?.value;
  } else {
    data.universityId = Number(dataToSave.university?.[0]?.id);
  }

  Sentry.addBreadcrumb({
    category: 'SignUp Data',
    message: JSON.stringify({ after: data, raw: dataToSave }),
    level: 'log',
  });

  if (data.majorAlt) {
    Sentry.captureMessage('SignUp with majorId, subjectId or universityId -1');
  }

  return data;
};

const SignUp = () => {
  const { setCurrentStep, dispatch, state, clearPersist } =
    useContext(QuestionPageContext);
  const registrationFeedback = useRegistrationFeedback({ state });
  const router = useRouter();
  registrationFeedback;
  const { elbProps } = useElb();

  const {
    control,
    formState: { isValid, isSubmitting, errors },
    setError,
    handleSubmit,
    watch,
  } = useForm<SignUpSchemaType>({
    mode: 'onChange',
    resolver: yupResolver(signUpSchema),
    defaultValues: state,
  });

  const { email } = watch();

  useEffect(() => {
    if (router.query.correctEmail === '1') {
      history.pushState(null, '', router.asPath);
      window.addEventListener('popstate', () =>
        history.pushState(null, '', router.asPath)
      );
    }
  }, [router.query.correctEmail]);

  const { loanLimit, isLoading } = useMaximumFinancing({
    majorId: Number(state?.major?.[0]?.id.toString()) ?? -1,
    graduationId: Number(state?.expectedDegree?.[0]?.id),
    fieldId: Number(state?.field?.[0]?.id.toString()) ?? -1,
  });

  const mutation = async (data: RegistrationSchemaType) => {
    const dbTrToken = window.sessionStorage.getItem('dbTrToken');
    const campaignToken = window.sessionStorage.getItem('campaignToken');
    const queryInput = mapQueryInput(data, dbTrToken, campaignToken);
    const response = await ApiClient.createUserData(queryInput);

    await cognitoSignIn(data?.email);

    exportToLocalStorage('customerId', response?.createUserData?.id);

    if (dbTrToken) {
      await fetch(
        `${process.env.NEXT_PUBLIC_DB_TRACKING_DOMAIN}/api/conversion/${dbTrToken}/${response?.createUserData?.id}`,
        {
          method: 'POST',
        }
      );
    }

    window.sessionStorage.removeItem('dbTrToken');
  };

  const submitMutation = useMutation(mutation, {
    onError: (error: any) => {
      let errorCode = error?.code || error;

      if (
        error.errors?.[0].message ===
        'An account with the given email already exists.'
      ) {
        setError('email', {
          type: TestTypes.EMAIL_ALREADY_EXISTS,
          message: ErrorMessages.EMAIL_ALREADY_EXISTS,
        });

        errorCode = 'UsernameExistsException';
      }

      handleCommonToast('error', errorCode, {}, error);
    },
  });

  const onSubmit = async (payload: SignUpSchemaType) => {
    if (email === state.email) {
      setError('email', {
        message: 'Die neue E-Mail muss eine andere sein als die vorherige',
      });
      return;
    }

    if (isSubmitting) {
      return;
    }
    dispatch({
      type: 'set',
      payload,
    });
    await submitMutation.mutateAsync({
      ...state,
      ...payload,
    });
    clearPersist();
    setCurrentStep('confirmEmail', 0);
  };

  if (registrationFeedback && registrationFeedback.allowToProceed === false) {
    router.replace(
      `/registration/rejected?t=${btoa(registrationFeedback.text)}`
    );
    return <div></div>;
  }

  return (
    <StudentQuestion noLimitHeight>
      <HeadContentSection>
        <Container>
          <HeadContentSection.Wrapper>
            <div>
              <H4Typography bold as="h1" color="grey7">
                Erste Einschätzung.
              </H4Typography>
              <H5Typography as="h2" bold color="grey7">
                Dein Studienvorhaben erlaubt wahrscheinlich eine Finanzierung im
                Rahmen von{' '}
                <BorderBottomSpan style={{ display: 'inline-block' }}>
                  {isLoading ? (
                    '...'
                  ) : (
                    <span
                      data-loan-min={loanLimit?.data?.getLoanLimit?.minLoan}
                    >
                      {formatPrice(
                        loanLimit?.data?.getLoanLimit?.minLoan || 3000,
                        {
                          currencyDisplay: 'name',
                        }
                      )}
                    </span>
                  )}{' '}
                  bis max{' '}
                  {isLoading ? (
                    '...'
                  ) : (
                    <span
                      data-loan-max={loanLimit?.data?.getLoanLimit?.maxLoan}
                    >
                      {formatPrice(
                        loanLimit?.data?.getLoanLimit?.maxLoan || 15000,
                        {
                          currencyDisplay: 'name',
                        }
                      )}
                    </span>
                  )}
                </BorderBottomSpan>{' '}
                Das prüfen wir jetzt genauer!
                <H6Typography
                  as="p"
                  color="functionalRed"
                  style={{ marginTop: '8px' }}
                >
                  {registrationFeedback && registrationFeedback.text}
                </H6Typography>
              </H5Typography>
            </div>
            <HeadContentSection.StyledImage
              src="/images/girlVibing.svg"
              alt="girl vibing"
              top="10px"
            />
          </HeadContentSection.Wrapper>
        </Container>
      </HeadContentSection>
      <div>
        <StudentQuestion.Section>
          <Container>
            <StudentQuestion.Wrapper>
              <H6Typography as="h3" bold color="grey7">
                Wie geht es weiter?
              </H6Typography>
              <H6Typography as="p" color="grey7" style={{ marginTop: '8px' }}>
                Für ein konkretes Angebot müssen wir deinen Finanzierungsbedarf
                kennen und brauchen weitere Informationen und Dokumente von dir.
              </H6Typography>
            </StudentQuestion.Wrapper>
          </Container>
        </StudentQuestion.Section>
        <StudentQuestion.Section>
          <Container style={{ marginBottom: '120px' }}>
            <StudentQuestion.Wrapper>
              <H5Typography as="h4" bold>
                <BorderBottomSpan>Melde dich an</BorderBottomSpan> um
                fortzufahren.
              </H5Typography>
              <RadioInputQuestion
                name={'gender'}
                list={genderList}
                control={control}
                errorMessage={ErrorMessages.GENDER_SELECT}
              />
              <TextInputQuestion
                name={'firstName'}
                control={control}
                placeholder="Vorname"
              />
              <TextInputQuestion
                name={'lastName'}
                control={control}
                placeholder="Name"
              />
              <DateInputQuestion
                name={'birthDate'}
                control={control}
                setError={setError}
              />
              <EmailInputQuestion
                name={'email'}
                errorMessage={errors.email?.message}
                control={control}
                errors={errors}
                placeholder={'E-Mail'}
              />
              <CheckboxInputQuestion
                name="checkbox"
                control={control}
                label={
                  <Sub2Typography>
                    Ja, ich habe die{' '}
                    <a
                      href="https://www.deutsche-bildung.de/datenschutz/"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: theme.colors.black,
                        textDecoration: 'underline',
                      }}
                    >
                      Datenschutzerklärung
                    </a>{' '}
                    gelesen und stimme ihr zu.
                  </Sub2Typography>
                }
              />
              <ButtonContainer columns={'1fr'}>
                <Button
                  isLoading={submitMutation.isLoading}
                  loader={<Loader />}
                  title={submitMutation.isLoading ? ' ' : 'Anmelden'}
                  buttonProps={{
                    ...elbProps,
                    'data-elbaction': TRACKING_ACTION.REGISTRATION.REGISTER,
                    as: 'button',
                    type: 'submit',
                    onClick: handleSubmit(onSubmit),
                  }}
                  disabled={!isValid || isSubmitting}
                />
              </ButtonContainer>
            </StudentQuestion.Wrapper>
          </Container>
        </StudentQuestion.Section>
      </div>
    </StudentQuestion>
  );
};

export default SignUp;
